// Syncs Home scroll position to header/nav accent using backdrop timeline segments.

import { useCallback, useContext, useLayoutEffect } from 'react';
import { useMotionValueEvent, type MotionValue } from 'framer-motion';
import { HomeNavAccentContext } from '../contexts/HomeNavAccentContext';
import type { ScrollBackdropSegment } from '../types/homeScroll';
import type { PlayerColorFamily } from '@/styles/playerColors';

/** Default lag for the first colored segment before header tint applies. */
export const HEADER_ACCENT_DEFAULT_LAG_VH = 14;

type BackdropSegmentForAccent = Pick<
  ScrollBackdropSegment,
  'startVh' | 'durationVh' | 'colorFamily' | 'headerAccentLagVh'
>;

/**
 * Returns the active segment's colorFamily when `scrollVh` falls within its
 * [startVh, startVh + durationVh) window. Later segments win on overlap.
 */
export function getActiveBackdropColorFamily(
  scrollVh: number,
  segments: readonly BackdropSegmentForAccent[]
): PlayerColorFamily | null {
  let active: PlayerColorFamily | null = null;
  for (const segment of segments) {
    const endVh = segment.startVh + segment.durationVh;
    if (scrollVh >= segment.startVh && scrollVh < endVh) {
      active = segment.colorFamily;
    }
  }
  return active;
}

/**
 * Header tint uses a delayed activation window so the logo/nav color trails
 * the backdrop transition slightly.
 */
export function getHeaderAccentColorFamily(
  scrollVh: number,
  segments: readonly BackdropSegmentForAccent[],
  defaultLagVh: number = HEADER_ACCENT_DEFAULT_LAG_VH
): PlayerColorFamily | null {
  let active: PlayerColorFamily | null = null;
  for (const segment of segments) {
    const endVh = segment.startVh + segment.durationVh;
    const lag = segment.headerAccentLagVh ?? defaultLagVh;
    const windowStart = segment.startVh + lag;
    if (scrollVh >= windowStart && scrollVh < endVh) {
      active = segment.colorFamily;
    }
  }
  return active;
}

/**
 * Home-only effect: subscribe to scrollVh and publish the active backdrop tint
 * to HomeNavAccentContext for Header consumption.
 */
export function useHomeColorSync(
  scrollVh: MotionValue<number>,
  segments: readonly ScrollBackdropSegment[]
): void {
  const setActiveColorFamily =
    useContext(HomeNavAccentContext)?.setActiveColorFamily;

  const sync = useCallback(
    (latest: number) => {
      if (!setActiveColorFamily) return;
      const next = getHeaderAccentColorFamily(latest, segments);
      setActiveColorFamily((prev) => (prev === next ? prev : next));
    },
    [segments, setActiveColorFamily]
  );

  useLayoutEffect(() => {
    if (!setActiveColorFamily) return;
    sync(scrollVh.get());
  }, [scrollVh, sync, setActiveColorFamily]);

  useMotionValueEvent(scrollVh, 'change', (latest) => {
    sync(latest);
  });

  useLayoutEffect(() => {
    if (!setActiveColorFamily) return;
    return () => {
      setActiveColorFamily(null);
    };
  }, [setActiveColorFamily]);
}
